/**
 * Export a conversation as a zip: transcript, artifacts and a manifest
 * describing the payload (tdd-0076 Phase 6).
 *
 * The pure parts — layout, naming, manifest construction — live here so they can
 * be tested without a browser, a zip library or a network. `buildConversationZip`
 * takes its fetching and zipping as injected functions for the same reason.
 *
 * TWO PROPERTIES ARE LOAD-BEARING:
 *
 * 1. **Filenames are human-meaningful.** A zip full of `a3f9c2e1.png` is
 *    technically complete and practically useless. Storage keys use uuids
 *    deliberately (collision safety, no attacker-influenced path), so the
 *    display name has to be restored on the way out.
 *
 * 2. **`omitted[]` is not decoration.** An artifact that could not be fetched
 *    must be named in the manifest. An export that silently drops a file while
 *    claiming completeness is worse than one that fails outright — the user
 *    finds out months later, when the original is gone.
 */
import type { ChatSession, Message, MessageAttachment } from '../types';

/** A file placed in the zip. */
export interface ZipEntry {
  /** Path within the archive. */
  path: string;
  /** Where the bytes come from, or the literal content for generated files. */
  source: { kind: 'storage'; key: string } | { kind: 'text'; content: string };
}

export interface ManifestFile {
  path: string;
  storageKey?: string;
  kind: 'upload' | 'generated' | 'edit' | 'mask' | 'transcript' | 'unlinked';
  displayName: string;
  bytes?: number;
  contentType?: string;
  messageId?: string;
  /** Parent's path WITHIN THE ZIP, so lineage is navigable after extraction. */
  derivedFrom?: string;
}

export interface ExportManifest {
  version: number;
  exportedAt: string;
  session: {
    id: string;
    title: string;
    createdAt: number;
    updatedAt: number;
    messageCount: number;
  };
  files: ManifestFile[];
  /** Anything not included, and why. Never omitted itself. */
  omitted: Array<{ storageKey: string; reason: string }>;
}

/**
 * The storage prefix holding everything a conversation produced.
 *
 * Re-stated here rather than imported from the artifact store so this module
 * stays free of React — it is otherwise pure and node-testable.
 */
export function sessionStoragePrefix(sessionId: string): string {
  return `${sessionId}/`;
}

/** Folder an artifact belongs in, derived from its key. */
export function folderForKey(key: string): ManifestFile['kind'] {
  if (key.includes('/masks/')) return 'mask';
  if (key.includes('/generated/')) return 'generated';
  if (key.includes('/uploads/')) return 'upload';
  return 'unlinked';
}

const FOLDER_NAMES: Record<ManifestFile['kind'], string> = {
  upload: 'uploads',
  generated: 'generated',
  edit: 'edits',
  mask: 'masks',
  transcript: '',
  unlinked: 'unlinked',
};

/**
 * Make a display name safe to use as a zip entry path.
 *
 * Zip-slip protection: the archive is extracted on the user's machine by
 * whatever tool they choose, and some are historically lax about `..`. A name
 * of `../../evil.sh` must land as `evil.sh` and nowhere else.
 *
 * Also strips control characters and collapses whitespace, since a filename
 * with a newline in it breaks some extractors in confusing ways.
 */
export function safeEntryName(displayName: string): string {
  const base = displayName
    // Take the last path segment: this alone defeats traversal.
    .split(/[/\\]/)
    .pop()!
    // Control characters (NUL, newline) break some extractors in confusing
    // ways, and are never intentional in a filename.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // `..` and `.` are not filenames; an empty result needs *something*.
  if (!base || base === '.' || base === '..') return 'file';
  return base;
}

/**
 * Give every entry a unique path, appending " (2)", " (3)" on collision.
 *
 * Two `screenshot.png` uploads in one conversation is ordinary — the storage
 * keys differ but the display names do not, and a zip with duplicate paths
 * either fails to build or silently loses one.
 */
export function uniquePath(taken: Set<string>, folder: string, name: string): string {
  const prefix = folder ? `${folder}/` : '';
  const candidate = `${prefix}${name}`;
  if (!taken.has(candidate)) {
    taken.add(candidate);
    return candidate;
  }

  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';

  for (let n = 2; ; n++) {
    const next = `${prefix}${stem} (${n})${ext}`;
    if (!taken.has(next)) {
      taken.add(next);
      return next;
    }
  }
}

/** Every attachment in a conversation that carries a storage key. */
export function attachmentsWithKeys(messages: Message[]): Array<{
  attachment: MessageAttachment;
  messageId: string;
}> {
  const out: Array<{ attachment: MessageAttachment; messageId: string }> = [];
  for (const message of messages) {
    for (const attachment of message.attachments ?? []) {
      if (attachment.storageKey) out.push({ attachment, messageId: message.id });
    }
  }
  return out;
}

/** The human-readable transcript, unchanged from the previous markdown export. */
export function buildTranscriptMarkdown(messages: Message[]): string {
  return messages
    .map((m) => {
      const role = m.role === 'user' ? 'User' : m.role === 'system' ? 'System' : 'Assistant';
      return `## ${role}\n\n${m.content}\n`;
    })
    .join('\n---\n\n');
}

export interface ExportPlan {
  entries: ZipEntry[];
  manifest: ExportManifest;
  /** Map from storage key to its zip path, for resolving lineage. */
  pathByKey: Map<string, string>;
}

/**
 * Decide what goes in the zip, before fetching anything.
 *
 * Separated from the fetching so the layout is testable on its own — and so a
 * failure to fetch changes only the manifest's `omitted[]`, never the shape of
 * the archive.
 *
 * `storageItems` is what the conversation's prefix actually contains. Anything
 * there but NOT referenced by a message lands in `unlinked/`: it exists, the
 * user paid to store it, and silently excluding it would make the export a
 * partial truth. (A generation whose tool result never made it back into the
 * transcript is the realistic case.)
 */
export function planExport(
  session: ChatSession,
  storageItems: Array<{ key: string; size?: number; contentType?: string; displayName?: string }>,
  now: string,
): ExportPlan {
  const taken = new Set<string>();
  const entries: ZipEntry[] = [];
  const files: ManifestFile[] = [];
  const pathByKey = new Map<string, string>();

  // Transcript first: it is the thing a human opens.
  const transcriptPath = uniquePath(taken, '', 'conversation.md');
  entries.push({
    path: transcriptPath,
    source: { kind: 'text', content: buildTranscriptMarkdown(session.messages) },
  });
  files.push({
    path: transcriptPath,
    kind: 'transcript',
    displayName: 'conversation.md',
  });

  const jsonPath = uniquePath(taken, '', 'conversation.json');
  entries.push({
    path: jsonPath,
    source: { kind: 'text', content: JSON.stringify(session, null, 2) },
  });
  files.push({ path: jsonPath, kind: 'transcript', displayName: 'conversation.json' });

  // Index the conversation's own attachments so storage items can be matched
  // back to the message that referenced them.
  const referenced = new Map<string, { attachment: MessageAttachment; messageId: string }>();
  for (const entry of attachmentsWithKeys(session.messages)) {
    referenced.set(entry.attachment.storageKey!, entry);
  }

  for (const item of storageItems) {
    const known = referenced.get(item.key);
    const kind = known ? folderForKey(item.key) : 'unlinked';
    const displayName =
      known?.attachment.name ?? item.displayName ?? item.key.split('/').pop() ?? 'file';

    const path = uniquePath(taken, FOLDER_NAMES[kind], safeEntryName(displayName));
    pathByKey.set(item.key, path);

    entries.push({ path, source: { kind: 'storage', key: item.key } });
    files.push({
      path,
      storageKey: item.key,
      kind,
      displayName,
      bytes: item.size,
      contentType: item.contentType,
      messageId: known?.messageId,
    });
  }

  // Resolve lineage to ZIP PATHS, not storage keys — the point is that the
  // extracted folder is navigable without reference to a live system.
  for (const file of files) {
    if (!file.storageKey) continue;
    const derivedFrom = referenced.get(file.storageKey)?.attachment.derivedFrom;
    if (derivedFrom) {
      const parentPath = pathByKey.get(derivedFrom);
      if (parentPath) file.derivedFrom = parentPath;
    }
  }

  return {
    entries,
    pathByKey,
    manifest: {
      version: 1,
      exportedAt: now,
      session: {
        id: session.id,
        title: session.title,
        createdAt: session.createdAt,
        updatedAt: session.updatedAt,
        messageCount: session.messages.length,
      },
      files,
      omitted: [],
    },
  };
}

/** Fetch bytes for a storage key, or null if unavailable. */
export type FetchArtifact = (key: string) => Promise<Blob | null>;

export interface ZipBuilder {
  file(path: string, data: Blob | string): void;
  generate(): Promise<Blob>;
}

/**
 * Bound concurrency when fetching artifacts.
 *
 * A conversation with 200 images would otherwise open 200 parallel requests and
 * make the browser, the storage API, or both, unhappy.
 */
const FETCH_CONCURRENCY = 6;

/**
 * Build the archive, recording anything that could not be fetched.
 *
 * Never throws for a missing artifact: the export completes and the manifest
 * says what is absent. A partial zip that documents its own gaps is far more
 * useful than a failed download, and the user learns which artifact is
 * unreachable — which is actionable.
 */
export async function buildConversationZip(
  plan: ExportPlan,
  fetchArtifact: FetchArtifact,
  zip: ZipBuilder,
): Promise<{ blob: Blob; manifest: ExportManifest }> {
  const manifest: ExportManifest = { ...plan.manifest, omitted: [...plan.manifest.omitted] };

  const storageEntries = plan.entries.filter(
    (e): e is ZipEntry & { source: { kind: 'storage'; key: string } } =>
      e.source.kind === 'storage',
  );

  for (const entry of plan.entries) {
    if (entry.source.kind === 'text') zip.file(entry.path, entry.source.content);
  }

  // Simple worker pool: take the next index until the queue is drained.
  let cursor = 0;
  const workers = Array.from({ length: Math.min(FETCH_CONCURRENCY, storageEntries.length) }, async () => {
    for (;;) {
      const index = cursor++;
      const entry = storageEntries[index];
      if (!entry) return;

      let blob: Blob | null = null;
      try {
        blob = await fetchArtifact(entry.source.key);
      } catch {
        blob = null;
      }

      if (blob) {
        zip.file(entry.path, blob);
      } else {
        manifest.omitted.push({
          storageKey: entry.source.key,
          reason: 'Could not be downloaded from storage.',
        });
        // Drop it from `files` too — listing a path that is not in the archive
        // would make the manifest describe an archive that does not exist.
        manifest.files = manifest.files.filter((f) => f.path !== entry.path);
      }
    }
  });

  await Promise.all(workers);

  // Written last so it reflects every omission.
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  return { blob: await zip.generate(), manifest };
}

/** Filename for the downloaded archive. */
export function exportFileName(session: ChatSession, now: Date): string {
  const stamp = now.toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const title = safeEntryName(session.title || 'conversation')
    .replace(/[^A-Za-z0-9 _-]/g, '')
    .trim()
    .slice(0, 60);
  return `${title || 'conversation'}-${stamp}.zip`;
}
